import { matchValueFor } from '../game/Chain.js';
import type { GameStateData } from '../game/GameState.js';
import { isDouble, tilePips, type Pip, type Tile } from '../game/types.js';
import type { DominoRuleSet } from '../game/variants/types.js';
import type { PlayMove } from './MoveEnumerator.js';
import { MoveEnumerator } from './MoveEnumerator.js';
import { suitCountsFor } from './SuitTracker.js';
import { deriveConstraints } from './PassConstraints.js';

/**
 * 9-feature linear evaluator from the M6 research summary. Scores a single
 * candidate play in the context of `state` from `seat`'s perspective — higher
 * is better for `seat`. Used by Medium-tier `DifficultyStrategy`; Easy tier
 * skips this and only weights immediate score + tile heaviness directly.
 *
 * Weights chosen by hand to land roughly in proportion with All Fives
 * literature: immediate score dominates (×10), going out is a near-terminal
 * bonus, and the defensive features (exposure / starve) are the main
 * tie-breakers when no scoring play is available.
 */

const W_IMMEDIATE = 10;
const W_HEAVY = 1;
const W_GOING_OUT = 1000;
const W_DOUBLE_HOLD = 3;
const W_SPINNER_SCORE = 12;
// Symmetric weight with immediate score: every expected point of opponent
// reply nets out one point of our own. Using *expected* (probabilistic)
// not *max* opponent score in `exposureScore` — using max made the AI
// over-defensive and worse than the pure-heuristic Easy tier in self-play.
const W_OPP_EXPOSURE = -10;
const W_STARVE = 3;
const W_PARTNER_FEED = 4;
const W_PASS_FORCE = 6;

export type EvalFeature =
  | 'immediateScore'
  | 'tileHeaviness'
  | 'goingOut'
  | 'handReduction'
  | 'doubleManagement'
  | 'opponentScoreExposure'
  | 'suitStarve'
  | 'partnershipFeed'
  | 'forcedPass';

export const ALL_FEATURES: ReadonlySet<EvalFeature> = new Set<EvalFeature>([
  'immediateScore',
  'tileHeaviness',
  'goingOut',
  'handReduction',
  'doubleManagement',
  'opponentScoreExposure',
  'suitStarve',
  'partnershipFeed',
  'forcedPass',
]);

export interface ScoreOptions {
  /** Restrict the linear combination to a subset of features. */
  readonly features?: ReadonlySet<EvalFeature>;
}

export class Evaluator {
  private readonly enumerator: MoveEnumerator;

  constructor(private readonly ruleSet: DominoRuleSet) {
    this.enumerator = new MoveEnumerator(ruleSet);
  }

  score(state: GameStateData, seat: number, move: PlayMove, opts?: ScoreOptions): number {
    const features = opts?.features ?? ALL_FEATURES;
    const handLen = state.hands[seat]?.length ?? 0;
    const handAfter = Math.max(0, handLen - 1);
    let total = 0;

    if (features.has('immediateScore')) {
      total += move.score * W_IMMEDIATE;
    }
    if (features.has('tileHeaviness')) {
      total += tilePips(move.tile) * W_HEAVY;
    }
    // `handReduction` was a no-op (every candidate removes exactly one tile,
    // so handAfter is constant across candidates). Feature still listed for
    // backward compatibility with the spec but does not contribute.
    if (features.has('goingOut') && handAfter === 0) {
      total += W_GOING_OUT;
    }
    if (features.has('doubleManagement')) {
      if (move.spinnerFormed && move.score > 0) total += W_SPINNER_SCORE;
      // Discourage burning a double early when it scores nothing — doubles
      // are awkward (single connection point) and best held for spinners.
      else if (isDouble(move.tile) && move.score === 0) total -= W_DOUBLE_HOLD;
    }

    // Counts after the play would still be approximately the same — the
    // played tile moves from inHand to onChain so the unseen counts (what
    // opponents collectively might hold) are unchanged. We can compute
    // counts once for the state and reuse for every candidate.
    const counts = suitCountsFor(state, seat);

    if (features.has('opponentScoreExposure')) {
      total += W_OPP_EXPOSURE * this.exposureScore(state, seat, move);
    }
    if (features.has('suitStarve')) {
      total += W_STARVE * this.starveBonus(move, counts);
    }
    if (features.has('forcedPass')) {
      total += W_PASS_FORCE * this.forcedPassBonus(move, counts);
    }
    if (features.has('partnershipFeed') && this.ruleSet.partnership) {
      total += W_PARTNER_FEED * this.partnershipFeed(state, seat, move);
    }
    return total;
  }

  /**
   * Expected scoring play the next opponent could make against `move.chainAfter`.
   * Sums each unseen tile's best response score and scales by the opponent's
   * share of the unseen pool — i.e. P(opp holds tile | tile unseen).
   *
   * Earlier max-based version weighted defense too heavily and made Medium
   * tier play *worse* than the pure-heuristic Easy tier in self-play.
   * Probability-weighted expectation is the standard fix for this class of
   * lookahead heuristic.
   */
  private exposureScore(state: GameStateData, seat: number, move: PlayMove): number {
    const nextSeat = (seat + 1) % state.playerCount;
    const constraints = deriveConstraints(state)[nextSeat] ?? new Set<Pip>();
    const ownHand = state.hands[seat] ?? [];
    const ownAfter = ownHand.filter((t) => t !== move.tile);
    const candidateOpponentTiles = unseenTilesFor(state, ownAfter);
    if (candidateOpponentTiles.length === 0) return 0;

    let totalBest = 0;
    for (const tile of candidateOpponentTiles) {
      if (constraints.has(tile[0]) && constraints.has(tile[1])) continue;
      let bestForTile = 0;
      for (const o of this.enumerator.enumerate(move.chainAfter, [tile])) {
        if (o.score > bestForTile) bestForTile = o.score;
      }
      totalBest += bestForTile;
    }
    const nextHandSize = state.hands[nextSeat]?.length ?? 0;
    return (totalBest * nextHandSize) / candidateOpponentTiles.length;
  }

  /** +1 per open-end pip the opponent likely cannot play (low unseen count). */
  private starveBonus(move: PlayMove, counts: ReturnType<typeof suitCountsFor>): number {
    let bonus = 0;
    const openPips = openPipsAfter(move);
    for (const pip of openPips) {
      // 0 unseen → certain starvation; clamp so we don't go negative.
      const unseen = counts[pip]?.unseen ?? 0;
      bonus += Math.max(0, 4 - unseen);
    }
    return bonus;
  }

  /**
   * Reward leaving the next seat with strictly zero legal plays — they will
   * be forced to draw (or pass). The constraint set from prior passes makes
   * this more decisive than `starveBonus` alone.
   */
  private forcedPassBonus(
    move: PlayMove,
    counts: ReturnType<typeof suitCountsFor>,
  ): number {
    const openPips = openPipsAfter(move);
    let totalUnseen = 0;
    for (const pip of openPips) totalUnseen += counts[pip]?.unseen ?? 0;
    // 0 unseen across all open pips → no tile in the world can play.
    if (totalUnseen === 0) return 3;
    if (totalUnseen <= 2) return 1;
    return 0;
  }

  /**
   * 2v2 partnership: leaving an open pip that the partner is *likely* to hold
   * (= high unseen + we already played some of that suit). Mirror of
   * `starveBonus` against the partner instead of the opponent.
   */
  private partnershipFeed(state: GameStateData, seat: number, move: PlayMove): number {
    const partnerSeat = state.teamMap.teamOf.findIndex((t, i) => t === state.teamMap.teamOf[seat] && i !== seat);
    if (partnerSeat < 0) return 0;
    const partnerCounts = suitCountsFor(state, partnerSeat);
    let bonus = 0;
    for (const pip of openPipsAfter(move)) {
      // High unseen from the partner's perspective is good (they might play).
      bonus += Math.min(2, partnerCounts[pip]?.unseen ?? 0);
    }
    return bonus;
  }
}

function openPipsAfter(move: PlayMove): readonly Pip[] {
  const seen = new Set<Pip>();
  for (const idx of move.chainAfter.activeArms) {
    const arm = move.chainAfter.arms[idx];
    if (!arm) continue;
    // matchValueFor returns the face of a double-outer (where openValue
    // carries the pip-sum for scoring), and arm.openValue otherwise. Reading
    // openValue directly would falsely report 10/12 for a [5,5]/[6,6] outer.
    const v = matchValueFor(arm);
    if (v >= 0 && v <= 6 && Number.isInteger(v)) seen.add(v as Pip);
  }
  return Array.from(seen);
}

/**
 * Tiles that might still be in any opponent's hand or boneyard. Set
 * semantics handle the lead's double-arm representation automatically.
 */
function unseenTilesFor(state: GameStateData, ownHandAfter: readonly Tile[]): readonly Tile[] {
  const seenKey = new Set<string>();
  for (const t of ownHandAfter) seenKey.add(`${t[0]}-${t[1]}`);
  for (const arm of state.chain.arms) {
    for (const t of arm.tiles) seenKey.add(`${t[0]}-${t[1]}`);
  }
  const out: Tile[] = [];
  for (let a = 0; a <= 6; a += 1) {
    for (let b = a; b <= 6; b += 1) {
      const key = `${a}-${b}`;
      if (!seenKey.has(key)) out.push([a as Pip, b as Pip]);
    }
  }
  return out;
}
